import moment from 'moment';
import { getConfig } from 'features/app/redux/features';
import { store } from '../store/store';

type AcademicYearStartDates = Record<string, string>;

type AcademicYearConfig = {
	year: number;
	endDate: Date;
};

/**
 * Returns the academic year end date config from Flagsmith.
 * Uses the app store directly because this utility is called outside the React component lifecycle.
 */
function academicYearEndDates(): AcademicYearStartDates | undefined {
	const state = store.getState();
	return getConfig('academic_year_end_dates_for_rollover')(state);
}

// Academic Year reference
export const academicYears: { [key: string]: number } = {
	y2000: 2000,
	y2001: 2001,
	y2002: 2002,
	y2003: 2003,
	y2004: 2004,
	y2005: 2005,
	y2006: 2006,
	y2007: 2007,
	y2008: 2008,
	y2009: 2009,
	y2010: 2010,
	y2011: 2011,
	y2012: 2012,
	y2013: 2013,
	y2014: 2014,
	y2015: 2015,
	y2016: 2016,
	y2017: 2017,
	y2018: 2018,
	y2019: 2019,
	y2020: 2020,
	y2021: 2021,
	y2022: 2022,
	y2023: 2023,
	y2024: 2024,
	y2025: 2025,
	y2026: 2026,
	y2027: 2027,
	y2028: 2028,
	y2029: 2029,
	y2030: 2030,
};

const yearStartDate = '08-01';
const yearEndDate = '07-31';

/**
 * return the long display name for the Academic Year
 * return string YYYY/YYYY
 * @param {number} year
 * @returns {string} Academic Year 'YYYY/YYYY'
 */
export function academicYearLongName(year: number): String {
	return `${year}/${year + 1}`;
}

/**
 * Returns the standard display name for the Academic Year - eg. 2017/18
 * The function attempts to convert argument to a number if one is not passed.
 * @param {number} year
 * @returns {string} Academic Year 'YYYY/YY'
 */
export function academicYearStandardFormat(year: number): String {
	var academicYear = year;

	return academicYear + '/' + (Number(academicYear) + 1).toString().substring(2);
}

/**
 * Returns the Academic Year as a string
 * @param {number} year
 * @returns {string} Academic Year 'YYYY'
 */
export function academicYearString(year: number): String {
	return (year + 1).toString();
}

/**
 * Returns the start date of the year
 * @param {number} year
 * @returns {moment} Academic Year start date
 */
export function getYearStart(year: number): moment.Moment {
	return moment(moment(`${year}-${yearStartDate} 00:00:00`).format('YYYY-MM-DD'));
}

/**
 * get the End of a MAT Academic year for use in the Monitoring Timeline only
 * @param {number} year
 * @returns {moment} MAT Academic Year end date
 */
export function getMatAcademicEnd(year: number): moment.Moment {
	const first = moment(`${year}-${yearEndDate} 00:00:00`);
	return moment(first.format('YYYY-MM-DD') + ' 00:00:00');
}

/**
 * get the start of MAT contract year
 * @param {number} year
 * @returns {moment} MAT Academic Year start date
 */
export function getMatYearStart(year: number): moment.Moment {
	return moment(`${year}-${yearStartDate} 00:00:00`);
}
/**
 * get the moment end of an Academic Year for MAT monitoring points
 * @param {number} year
 * @returns {moment} MAT Academic Year end date
 */
export function getMatYearEnd(year: number): moment.Moment {
	return moment(getMatAcademicEnd(year + 1).format('YYYY-MM-DD') + ' 23:59:59.99');
}

/**
 * get the moment end of an Academic Year
 * @param {number} year
 * @returns {moment} year end date
 */
export function getYearEnd(year: number): moment.Moment {
	return moment(
		getYearStart(year + 1)
			.add(-1, 'days')
			.format('YYYY-MM-DD') + ' 23:59:59.99'
	);
}

/**
 * A school counts towards the DfE MAT performance measures once it has been with
 * the MAT for at least 3 years, i.e. it joined on or before the date 3 years prior
 * to today.
 * Mirrors the server-side rule in Yeti's SchoolContractService — used only for
 * optimistic display; the server-derived value wins on the next fetch.
 * @param {string | null} joinedMatDate
 * @returns {boolean}
 */
export function isDfeEligible(joinedMatDate?: string | null): boolean {
	if (!joinedMatDate) return false;
	return moment(joinedMatDate).isSameOrBefore(moment().subtract(3, 'years'), 'day');
}

/**
 * return an enumarable array of the Academic Years
 * @returns {array<int>}
 */
export function academicYearsEnumarable(): number[] {
	return Object.keys(academicYears).map((ay) => {
		return academicYears[ay];
	});
}

/**
 * Converts a configured academic year key, e.g. "y2026", into the numeric year.
 */
function parseAcademicYearKey(key: string): number | undefined {
	const match = /^y(\d{4})$/.exec(key);
	const year = match ? Number(match[1]) : undefined;

	return year && !Number.isNaN(year) ? year : undefined;
}

/**
 * Converts a configured date string into a local midnight Date.
 */
function parseAcademicYearEndDate(dateStr: string | undefined): Date | undefined {
	if (!dateStr) {
		return undefined;
	}

	const date = new Date(`${dateStr}T00:00:00`);

	return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Returns valid configured academic years with parsed end dates.
 *
 * Each year's configured date marks the end of that academic year — the point
 * at which the next year begins.
 */
function getConfiguredAcademicYears(): AcademicYearConfig[] {
	const endDates = academicYearEndDates();

	if (!endDates) {
		return [];
	}

	return Object.entries(endDates)
		.map(([key, endDate]) => ({
			year: parseAcademicYearKey(key),
			endDate: parseAcademicYearEndDate(endDate),
		}))
		.filter(
			(academicYear): academicYear is AcademicYearConfig =>
				academicYear.year !== undefined && academicYear.endDate !== undefined
		);
}

/**
 * Returns configured academic years that are past or currently active.
 *
 * Historical years have an end date that has already passed. The current year
 * is the one with the nearest end date that is still in the future. Years
 * whose end dates are further in the future are excluded until they become current.
 */
function getActiveConfiguredYears(date: Date): AcademicYearConfig[] {
	const allYears = getConfiguredAcademicYears().sort(
		(a, b) => a.endDate.getTime() - b.endDate.getTime()
	);

	const pastYears = allYears.filter(({ endDate }) => endDate <= date);
	const currentYear = allYears.find(({ endDate }) => endDate > date);

	return currentYear ? [...pastYears, currentYear] : pastYears;
}

/**
 * Gets the active academic year without jumping over inactive years.
 *
 * This lets early-enabled future years appear in selectors without becoming the
 * default selected academic year.
 */
function getCurrentConfiguredAcademicYear(date: Date): number | undefined {
	const activeConfiguredYears = getActiveConfiguredYears(date)
		.map(({ year }) => year)
		.sort((a, b) => a - b);

	const activeYears = [...new Set(activeConfiguredYears)];
	const firstActiveYear = activeYears[0];

	if (firstActiveYear === undefined) {
		return undefined;
	}

	return activeYears.reduce((currentYear, year) => {
		return year === currentYear || year === currentYear + 1 ? year : currentYear;
	}, firstActiveYear);
}

/**
 * Resolves the default academic year when no usable config is available.
 */
function getDefaultAcademicYear(date: Date): number {
	const year = date.getFullYear();
	const defaultAcademicYearStart = new Date(`${year}-07-01T00:00:00`);

	return date < defaultAcademicYearStart ? year - 1 : year;
}

/**
 * Resolves the academic year for a date using the configured end dates.
 *
 * Returns the highest contiguous configured academic year that has not yet ended,
 * falling back to a July-based default if no config is present.
 */
function fromDate(date: Date): number {
	return getCurrentConfiguredAcademicYear(date) ?? getDefaultAcademicYear(date);
}

/**
 * Gets the current academic year using today's date and the configured end dates.
 */
export const getCurrentAcademicYear = (): number => {
	return fromDate(new Date());
};

/** Label/value pair returned to UI selectors; rollover date is stripped after filtering. */
type NewGroupReportsYearOption = {
	label: string;
	value: number;
};

/** Validated config entry with parsed rollover date, used only while filtering and sorting. */
type ParsedNewGroupReportsYear = NewGroupReportsYearOption & {
	rolloverDate: Date;
};

/**
 * Validates and parses a new group reports config entry.
 */
function parseNewGroupReportsYearEntry(
	key: string,
	entry: Features.NewGroupReportsAcademicYear
): ParsedNewGroupReportsYear | undefined {
	if (parseAcademicYearKey(key) === undefined) {
		return undefined;
	}

	const rolloverDate = parseAcademicYearEndDate(entry?.date);

	if (
		!rolloverDate ||
		typeof entry?.label !== 'string' ||
		typeof entry?.value !== 'number' ||
		Number.isNaN(entry.value)
	) {
		return undefined;
	}

	return {
		label: entry.label,
		value: entry.value,
		rolloverDate,
	};
}

/**
 * Returns available academic year options for new group reports.
 * Only includes years whose rollover date is equal to or before the given date (i.e: today's date).
 * Sort newest first.
 */
export function getNewGroupReportsAvailableYears(
	date: Date = new Date(),
	config?: Record<string, Features.NewGroupReportsAcademicYear>
): NewGroupReportsYearOption[] {
	if (!config) {
		return [];
	}

	return Object.entries(config)
		.map(([key, entry]) => parseNewGroupReportsYearEntry(key, entry))
		.filter((year): year is ParsedNewGroupReportsYear => year !== undefined)
		.filter(({ rolloverDate }) => rolloverDate <= date)
		.sort((a, b) => b.value - a.value)
		.map(({ label, value }) => ({ label, value }));
}

/**
 * get an Academic Year from a "YYYY" start string
 * @param {number} year to find
 * @returns {number} academic year
 * @throws {Error} 'no such Academic Year'
 */
function fromStartYearString(year: number): number {
	if (academicYears[`y${year}`]) {
		return academicYears[`y${year}`];
	} else {
		throw new Error('no such Academic Year');
	}
}

/**
 * Extracts the academic year from a slash-separated string by parsing the first segment.
 * Works with various formats where the first part is the year (e.g. "YYYY/MM/DD" or "YYYY/YYYY").
 * @param {string} yearString - A slash-separated string where the first part is the year (e.g. "2019/12/12" or "2017/2018")
 * @returns {number} academic year
 * @throws {Error} 'no such Academic Year' if the extracted year is not a valid academic year
 */
export function getAcademicYearFromYearString(yearString: string): number {
	return fromStartYearString(Number.parseInt(yearString.split('/')[0]));
}

/**
 * Builds an ascending list of academic years going back `previousYears` from the current year.
 *
 * The range always starts at `currentYear - previousYears`. If `endYear` is greater than
 * `currentYear` the range extends forward to `endYear`; otherwise it ends at `currentYear`.
 * A negative `previousYears` produces an empty array.
 */
export const enumerateBackFromYear = (previousYears = 1, endYear = null) => {
	const currentYear: number = endYear ?? getCurrentAcademicYear();
	const startYear: number = currentYear - previousYears;

	const actualEndYear: number = endYear > currentYear ? endYear : currentYear;

	if (startYear > actualEndYear) {
		return [];
	}

	return Array.from({ length: actualEndYear - startYear + 1 }, (_, i) => startYear + i);
};
